// src/pricing.js
//
// What a delivery code actually does to a price, in one place.
//
// A code carries one rule per product, and a rule answers two questions:
//   * how the price changes — a percentage off, or a fixed price per unit;
//   * how many units it changes — the first N, after which the customer pays
//     the ordinary price again.
//
// So "Teff at 50%, 8 units max" charges half price on eight bales and full
// price on the ninth. The cap is per product, not per order: eight Teff and
// eight Lucerne are eight discounted units each.
//
// The storefront, the admin dashboard and the checkout endpoint all price an
// order through these functions, so the number the customer is shown, the
// number the server charges and the number the dashboard reports are the same
// number. functions/pricing.js is a CommonJS mirror of this file — the Cloud
// Functions package cannot import out of src/, so the two are kept in step by
// hand. Change one, change the other.

/** A percentage taken off the ordinary price. */
export const PERCENT = 'percent';

/** A fixed rand price per unit, replacing the ordinary price. */
export const UNIT_PRICE = 'price';

export const DISCOUNT_TYPES = [PERCENT, UNIT_PRICE];

export const DISCOUNT_TYPE_LABELS = {
  [PERCENT]: 'Percent off',
  [UNIT_PRICE]: 'Price per unit',
};

/** No cap. Stored as 0 rather than absent so an admin can see it is deliberate. */
export const UNLIMITED_UNITS = 0;

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Codes written before types existed carry a percentage and nothing else. */
export const normaliseType = (type) => (type === UNIT_PRICE ? UNIT_PRICE : PERCENT);

/**
 * The rule a source document holds for one product, in a shape the rest of the
 * file can rely on.
 *
 * `source` is anything carrying the four maps — a discountCodes document or an
 * order, which stores its own copy so it stays priced the way it was sold even
 * after the code is edited or deleted.
 *
 * `toMoney` converts the stored rand amounts into whatever unit the caller is
 * working in; the checkout endpoint passes a rands-to-cents converter so the
 * whole calculation stays in integer cents.
 */
export function discountRule(source, id, toMoney = (value) => value) {
  const percent = num(source?.discountPercents?.[id]);
  const unitPrice = num(source?.discountUnitPrices?.[id]);
  const maxUnits = Math.max(0, Math.floor(num(source?.discountMaxUnits?.[id])));
  const type = normaliseType(source?.discountTypes?.[id]);
  return {
    type,
    percent: Math.min(100, Math.max(0, percent)),
    unitPrice: Math.max(0, toMoney(unitPrice)),
    maxUnits,
  };
}

/** Every product's rule, keyed by id. */
export function discountRules(source, productIds, toMoney) {
  const rules = {};
  productIds.forEach((id) => {
    rules[id] = discountRule(source, id, toMoney);
  });
  return rules;
}

/**
 * Whether a rule changes the price at all.
 *
 * A percentage of zero does nothing. A unit price only does something when it
 * is below the ordinary price, which is not known here, so a unit-price rule
 * counts as set whenever the admin has entered one — including R0, which is
 * how a code gives a product away.
 */
export function ruleIsActive(rule) {
  if (!rule) return false;
  if (rule.type === UNIT_PRICE) return true;
  return rule.percent > 0;
}

/** How many of the units bought the rule reaches. The rest are at full price. */
export function discountedUnits(qty, rule) {
  const quantity = Math.max(0, num(qty));
  const cap = rule?.maxUnits || UNLIMITED_UNITS;
  return cap > 0 ? Math.min(quantity, cap) : quantity;
}

/** The units beyond the cap, charged at the ordinary price. */
export function fullPriceUnits(qty, rule) {
  return Math.max(0, Math.max(0, num(qty)) - discountedUnits(qty, rule));
}

/**
 * What comes off one product line, in the same unit as `unitPrice`.
 *
 * Unrounded: the caller decides, because the checkout works in whole cents and
 * the storefront is only drawing a figure on a page.
 */
export function lineDiscount(qty, unitPrice, rule) {
  const price = Math.max(0, num(unitPrice));
  const units = discountedUnits(qty, rule);
  if (units <= 0 || price <= 0 || !ruleIsActive(rule)) return 0;

  if (rule.type === UNIT_PRICE) {
    // A "discount" that raises the price is not one — a code can only ever
    // take money off, so a unit price above the ordinary one changes nothing.
    const target = Math.min(Math.max(0, rule.unitPrice), price);
    return units * (price - target);
  }
  return units * price * (rule.percent / 100);
}

/**
 * One product line, priced.
 *
 * `round` is applied to the discount — the checkout passes Math.round to keep
 * everything in whole cents.
 */
export function priceLine(qty, unitPrice, rule, round = (value) => value) {
  const quantity = Math.max(0, num(qty));
  const price = Math.max(0, num(unitPrice));
  const gross = quantity * price;
  const discount = round(lineDiscount(quantity, price, rule));
  const units = discountedUnits(quantity, rule);
  return {
    qty: quantity,
    unitPrice: price,
    gross,
    discount,
    total: gross - discount,
    discountedUnits: units,
    fullPriceUnits: quantity - units,
    // True when the customer bought past the cap, which is the one thing about
    // this that surprises people and so is worth saying out loud.
    capReached: (rule?.maxUnits || 0) > 0 && quantity > rule.maxUnits,
    rule,
  };
}

/**
 * Prices a whole basket against a source document.
 *
 * `quantities` and `unitPrices` are keyed by product id.
 */
export function priceBasket({
  productIds,
  quantities,
  unitPrices,
  source,
  toMoney = (value) => value,
  round = (value) => value,
}) {
  const lines = {};
  let subtotal = 0;
  let discount = 0;

  productIds.forEach((id) => {
    const line = priceLine(
      quantities?.[id],
      unitPrices?.[id],
      discountRule(source, id, toMoney),
      round
    );
    lines[id] = line;
    subtotal += line.gross;
    discount += line.discount;
  });

  return { lines, subtotal, discount, total: subtotal - discount };
}

/**
 * The rule in words, for a chip or a table cell. `money` formats an amount the
 * way the surrounding page already formats money.
 */
export function describeRule(rule, money = (value) => `R${num(value).toFixed(2)}`) {
  if (!ruleIsActive(rule)) return '';
  const what = rule.type === UNIT_PRICE ? `${money(rule.unitPrice)}/unit` : `${rule.percent}%`;
  return rule.maxUnits > 0 ? `${what}, first ${rule.maxUnits}` : what;
}

/**
 * Collapses per-product admin form state into the four maps stored on a code or
 * an order. Products with no rule are left out entirely, so a document only
 * ever names the products a code actually touches.
 *
 * `rules` is keyed by product id: { type, percent, unitPrice, maxUnits }.
 */
export function toDiscountMaps(rules, productIds) {
  const discountPercents = {};
  const discountTypes = {};
  const discountUnitPrices = {};
  const discountMaxUnits = {};

  productIds.forEach((id) => {
    const rule = rules?.[id];
    if (!rule) return;
    const type = normaliseType(rule.type);
    const percent = num(rule.percent);
    const unitPrice = num(rule.unitPrice);
    const maxUnits = Math.max(0, Math.floor(num(rule.maxUnits)));

    if (type === UNIT_PRICE) {
      if (rule.unitPrice === '' || rule.unitPrice == null) return;
      discountTypes[id] = UNIT_PRICE;
      discountUnitPrices[id] = unitPrice;
    } else {
      if (percent <= 0) return;
      discountPercents[id] = percent;
    }
    if (maxUnits > 0) discountMaxUnits[id] = maxUnits;
  });

  return { discountPercents, discountTypes, discountUnitPrices, discountMaxUnits };
}

/** The reverse: form state for one product, read off a stored document. */
export function toRuleForm(source, id) {
  const type = normaliseType(source?.discountTypes?.[id]);
  const percent = num(source?.discountPercents?.[id]);
  const unitPrice = num(source?.discountUnitPrices?.[id]);
  const maxUnits = Math.max(0, Math.floor(num(source?.discountMaxUnits?.[id])));
  return {
    type,
    percent: percent ? String(percent) : '',
    unitPrice: type === UNIT_PRICE && source?.discountUnitPrices?.[id] != null ? String(unitPrice) : '',
    maxUnits: maxUnits ? String(maxUnits) : '',
  };
}

/**
 * Validates one product's rule as typed into a form.
 * Returns an error string, or null when the rule is usable.
 */
export function validateRuleForm(rule, productName) {
  const type = normaliseType(rule?.type);
  const maxUnits = Number(rule?.maxUnits);
  if (rule?.maxUnits !== '' && rule?.maxUnits != null) {
    if (!Number.isFinite(maxUnits) || maxUnits < 0 || !Number.isInteger(maxUnits)) {
      return `The unit limit for ${productName} must be a whole number.`;
    }
  }
  if (type === UNIT_PRICE) {
    if (rule.unitPrice === '' || rule.unitPrice == null) return null;
    const price = Number(rule.unitPrice);
    if (!Number.isFinite(price) || price < 0) {
      return `Check the price per unit for ${productName}.`;
    }
    return null;
  }
  if (rule.percent === '' || rule.percent == null) return null;
  const percent = Number(rule.percent);
  if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
    return `Check the percentage for ${productName}.`;
  }
  return null;
}
